import { useCallback, useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fieldErrs } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { useHub } from "@/components/AppShell";
import { Field, Loader, PageHeader } from "@/components/common";
import PiggyStage from "@/components/piggy/PiggyStage";
import GlassPiggy from "@/components/piggy/GlassPiggy";
import { FormDialog, KIDS_RGB } from "@/components/kids/kidsUi";
import { useLiveRefresh } from "@/lib/live";
import JarsPanel from "@/components/piggy/JarsPanel";
import JarSettings from "@/components/piggy/JarSettings";

const JAR_FILTERS = [["all", "All"], ["save", "Save"], ["spend", "Spend"], ["give", "Give"]];

const REASONS = [{ id: "cash_out", label: "Cash paid out" }, { id: "spent", label: "Spent" }, { id: "correction", label: "Correction" }];

function MoneyDialog({ mode, kid, hubId, onClose, onSaved }) {
  const [f, setF] = useState({ amount: "", note: "", reason: "cash_out" });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const save = async () => {
    setError("");
    const errs = {};
    if (!(Number(f.amount) > 0)) errs.amount = "Amount must be more than $0";
    if (mode === "add" && !f.note.trim()) errs.note = "Add a note";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    try {
      const { data } = await api.post(`/organizations/${hubId}/piggy-bank/${kid.id}/${mode}`, { ...f, amount: Number(f.amount) });
      toast.success(mode === "add" ? `Added ${money(f.amount)}` : `Took ${money(f.amount)}`);
      onSaved(data);
    } catch (e) { setErrors(fieldErrs(e) || {}); if (!fieldErrs(e)) setError(errMsg(e)); }
  };
  return (
    <FormDialog open onClose={onClose} title={mode === "add" ? `Add money to ${kid.name}` : `Take money from ${kid.name}`} testid={`money-${mode}-dialog`}>
      <div className="space-y-4">
        <Field label="Amount ($)" htmlFor="money-amount" error={errors.amount} errorTestid="money-amount-error">
          <input id="money-amount" type="number" step="0.01" min="0.01" className="q-input" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="money-amount-input" />
        </Field>
        {mode === "take" && (
          <Field label="Reason">
            <div className="flex flex-wrap gap-2">{REASONS.map((r) => <button key={r.id} type="button" className={`q-btn py-2 text-xs ${f.reason === r.id ? "q-btn-primary" : "q-btn-secondary"}`} onClick={() => setF({ ...f, reason: r.id })} data-testid={`money-reason-${r.id}`}>{r.label}</button>)}</div>
          </Field>
        )}
        <Field label={mode === "add" ? "Note" : "Note (optional)"} htmlFor="money-note" error={errors.note} errorTestid="money-note-error">
          <input id="money-note" className="q-input" maxLength={80} value={f.note} placeholder={mode === "add" ? "Birthday from Grandma" : ""} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="money-note-input" />
        </Field>
        {error && <p className="q-error" data-testid="money-error">{error}</p>}
        <div className="flex justify-end gap-2">
          <button className="q-btn q-btn-ghost" onClick={onClose} data-testid="money-cancel-btn">Cancel</button>
          <button className="q-btn q-btn-primary" onClick={save} data-testid="money-save-btn">{mode === "add" ? "Add money" : "Take money"}</button>
        </div>
      </div>
    </FormDialog>
  );
}

function HistoryList({ rows }) {
  if (!rows.length) return <p className="text-sm text-q-muted" data-testid="piggy-history-empty">No money yet. Finish chores to earn some!</p>;
  return (
    <ul className="q-card divide-y divide-q-border p-0" data-testid="piggy-history">
      {rows.map((e) => (
        <li key={e.id} className="flex items-center gap-3 px-4 py-3" data-testid={`ledger-row-${e.id}`}>
          <span className="w-16 flex-shrink-0 text-xs font-semibold text-q-faint">{fmtDate(e.day, { month: "short", day: "numeric" })}</span>
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-q-text" data-testid={`ledger-label-${e.id}`}>{e.label}</span>
          <span className="text-sm font-extrabold tabular-nums" style={{ color: e.amount >= 0 ? "rgb(var(--glow-money))" : "rgb(var(--q-danger-text))" }} data-testid={`ledger-amount-${e.id}`}>
            {e.amount >= 0 ? "+" : "−"}{money(Math.abs(e.amount))}
          </span>
        </li>
      ))}
    </ul>
  );
}

function KidPiggy({ hubId, kidId, onBank }) {
  return (
    <div className="mb-8 rounded-3xl border-2 bg-q-surface px-4 py-6 q-rise" style={{ borderColor: `rgba(${KIDS_RGB},0.35)`, boxShadow: `0 0 40px rgba(${KIDS_RGB},0.15)` }} data-testid="big-piggy">
      <PiggyStage hubId={hubId} kidId={kidId} size="lg" poll controls onBank={onBank} />
    </div>
  );
}

export default function PiggyBankPage() {
  const { hub } = useHub();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [jarF, setJarF] = useState("all");
  const load = useCallback(() => api.get(`/organizations/${hub.id}/piggy-bank`).then(({ data: d }) => setData(d)), [hub.id]);
  const onKidBank = useCallback((b) => setData((d) => (d ? { ...d, kids: [b] } : d)), []);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(() => load().catch(() => {}), (m) => ["piggy.ledger", "chore", "kid_request"].includes(m.entity), null);
  if (!data) return <Loader />;
  const isKid = hub.my_role === "kid";
  const bank = data.kids.find((k) => k.kid.id === sel) || (isKid ? data.kids[0] : null);

  return (
    <div data-testid="piggy-page">
      <PageHeader eyebrow="Kids Corner" title="Piggy Bank" rgb={KIDS_RGB} testid="piggy-title" />
      {isKid ? (data.kids[0].jars?.on ? (
        <div className="mb-8 rounded-3xl border-2 bg-q-surface px-4 py-6 q-rise" style={{ borderColor: `rgba(${KIDS_RGB},0.35)` }} data-testid="big-piggy">
          <JarsPanel hubId={hub.id} bank={data.kids[0]} isKid onChanged={load} onBank={onKidBank} />
        </div>
      ) : <KidPiggy hubId={hub.id} kidId={data.kids[0].kid.id} onBank={onKidBank} />) : (
        <div className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data.kids.map((k) => (
            <button key={k.kid.id} onClick={() => setSel(k.kid.id)} data-testid={`piggy-kid-${k.kid.name.toLowerCase()}`} data-active={sel === k.kid.id ? "1" : "0"}
              className="q-card flex items-center gap-3 text-left transition-[border-color,transform] duration-150 hover:-translate-y-0.5"
              style={{ borderColor: sel === k.kid.id ? `rgb(${KIDS_RGB})` : undefined }}>
              <GlassPiggy balance={k.balance} tint={k.style.tint} accessory={k.style.accessory} size="sm" testid={`mini-piggy-${k.kid.name.toLowerCase()}`} />
              <div className="min-w-0 flex-1">
                <p className="font-display text-lg font-extrabold text-q-text">{k.kid.name}</p>
                <p className="text-xs text-q-muted">Earned this week {money(k.earned_this_week)}</p>
                {k.jars?.on && <p className="text-xs font-semibold text-q-text" data-testid={`piggy-kid-jars-${k.kid.name.toLowerCase()}`}>Spend {money(k.jars.spend)} · Save {money(k.jars.save)} · Give {money(k.jars.give)} · Total {money(k.jars.total)}</p>}
              </div>
              <span className="font-display text-2xl font-extrabold text-q-text" data-testid={`piggy-kid-balance-${k.kid.name.toLowerCase()}`}>{money(k.balance)}</span>
            </button>
          ))}
        </div>
      )}
      {bank && (
        <section data-testid="piggy-kid-detail">
          {!isKid && data.can_manage && <JarSettings hubId={hub.id} bank={bank} onChanged={load} />}
          {!isKid && bank.jars?.on && <div className="q-card mb-6"><JarsPanel hubId={hub.id} bank={bank} isKid={false} onChanged={load} /></div>}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="q-eyebrow">{isKid ? <>History · <span style={{ color: "rgb(var(--glow-money))" }} data-testid="piggy-earned-week">Earned this week: {money(bank.earned_this_week)}</span></> : `${bank.kid.name}'s history · ${money(bank.balance)}`}</h2>
            {data.can_manage && (
              <div className="flex gap-2">
                <button className="q-btn q-btn-primary" onClick={() => setDialog("add")} data-testid="piggy-add-btn"><Plus className="h-4 w-4" /> Add money</button>
                <button className="q-btn q-btn-secondary" onClick={() => setDialog("take")} data-testid="piggy-take-btn"><Minus className="h-4 w-4" /> Take money</button>
              </div>
            )}
          </div>
          {bank.jars?.on && <div className="mb-3 flex flex-wrap gap-2" data-testid="history-jar-filter">{JAR_FILTERS.map(([v, l]) => <button key={v} className={`q-btn min-h-[56px] ${jarF === v ? "q-btn-primary" : "q-btn-secondary"}`} onClick={() => setJarF(v)} data-testid={`history-filter-${v}`}>{l}</button>)}</div>}
          <HistoryList rows={bank.history.filter((e) => jarF === "all" || e.jar === jarF)} />
          {jarF !== "all" && <p className="mt-2 text-sm font-bold text-q-text" data-testid="history-filter-sum">{JAR_FILTERS.find((x) => x[0] === jarF)[1]} total {money(bank.jar_sums[jarF])}</p>}
        </section>
      )}
      {!isKid && !bank && <p className="text-sm text-q-muted q-onwall" data-testid="piggy-pick-kid">Pick a kid to see their history.</p>}
      {dialog && bank && <MoneyDialog mode={dialog} kid={bank.kid} hubId={hub.id} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
    </div>
  );
}
